import { useEffect, useRef, useState } from "react";

import type { BillingOptions, BillingProduct } from "../../shared/api/client";
import { messages, useLocale, useT, type MessageKey } from "../../shared/i18n";
import { Button, ListRow } from "../../shared/ui";
import { checkoutErrorKey, formatMinor, gotServerResponse } from "./sbpFormat";

/** Same states as the Stars upgrade, plus the two SBP-only endings. */
type SbpState =
  | "idle"
  | "choosing"
  | "opening"
  | "confirming"
  | "processing"
  | "paid"
  | "failed"
  | "expired";

const CONFIRMATION_TIMEOUT_MS = 90_000;
const REFRESH_INTERVAL_MS = 5_000;

export function SbpUpgrade({
  options,
  planId,
  entitlementSignature,
  onPay,
  onCheckOrder,
  onRefresh,
}: {
  options: BillingOptions | null | undefined;
  planId: string | null;
  /** Changes when the server records a new Pro period or pack. */
  entitlementSignature: string;
  /** Opens the provider link; resolves once it has been handed over. */
  onPay: (idempotencyKey: string, productId: string) => Promise<string | null>;
  /** State of the order that `onPay` opened; polled while confirming. */
  onCheckOrder?: (orderId: string) => Promise<string>;
  /** Asks the backend again; the entitlement is only ever observed there. */
  onRefresh?: () => Promise<void>;
}) {
  const t = useT();
  const { locale } = useLocale();
  const [state, setState] = useState<SbpState>("idle");
  const [errorKey, setErrorKey] = useState<MessageKey>("profile.upgradeFailed");
  const keys = useRef(new Map<string, string>());
  const baseline = useRef(entitlementSignature);
  const orderId = useRef<string | null>(null);
  const activeProduct = useRef<string | null>(null);
  // A finished attempt must not pin the next tap to its (now spent) order.
  const dropKey = () => {
    if (activeProduct.current !== null) {
      keys.current.delete(activeProduct.current);
    }
  };
  // Callbacks are inline in the parent; refs keep the timers from restarting
  // on every render (a profile re-fetch re-renders the parent).
  const checkOrder = useRef(onCheckOrder);
  const refresh = useRef(onRefresh);
  useEffect(() => {
    checkOrder.current = onCheckOrder;
    refresh.current = onRefresh;
  });

  useEffect(() => {
    if (state === "confirming" && entitlementSignature !== baseline.current) {
      dropKey();
      setState("paid");
    }
  }, [state, entitlementSignature]);

  useEffect(() => {
    if (state !== "confirming") return;
    const live: { current: boolean } = { current: true };
    const isLive = () => live.current;
    const timeout = window.setTimeout(() => {
      setState("processing");
    }, CONFIRMATION_TIMEOUT_MS);
    const tick = async () => {
      const id = orderId.current;
      const check = checkOrder.current;
      if (id !== null && check) {
        const orderState = await check(id);
        if (!isLive()) return;
        if (orderState === "expired" || orderState === "failed") {
          dropKey();
          setErrorKey("billing.sbp.failed");
          setState(orderState === "failed" ? "failed" : "expired");
          return;
        }
        // "paid" is confirmed; the profile is still re-read so the new
        // entitlement shows. The signature effect then ends the wait.
        await refresh.current?.();
        if (isLive() && orderState === "paid") {
          dropKey();
          setState("paid");
        }
        return;
      }
      await refresh.current?.();
    };
    const poll = window.setInterval(() => {
      void tick().catch(() => undefined);
    }, REFRESH_INTERVAL_MS);
    return () => {
      live.current = false;
      window.clearTimeout(timeout);
      window.clearInterval(poll);
    };
  }, [state]);

  if (options?.channels.includes("sbp") !== true) return null;
  const products = options.products.filter(
    (product) =>
      product.prices.sbp !== undefined &&
      !(product.kind === "subscription" && planId === "pro"),
  );
  if (products.length === 0) return null;

  const pay = async (product: BillingProduct) => {
    setState("opening");
    baseline.current = entitlementSignature;
    orderId.current = null;
    activeProduct.current = product.id;
    try {
      let key = keys.current.get(product.id);
      if (key === undefined) {
        key = crypto.randomUUID();
        keys.current.set(product.id, key);
      }
      orderId.current = (await onPay(key, product.id)) ?? null;
      setState("confirming");
    } catch (error) {
      console.error("SBP checkout failed", error);
      // Any server answer ends the attempt; only a silent network failure
      // keeps the key so a retry lands on the same order.
      if (gotServerResponse(error)) dropKey();
      setErrorKey(checkoutErrorKey(error));
      setState("failed");
    }
  };
  const title = (product: BillingProduct) =>
    product.title_key in messages
      ? t(product.title_key as MessageKey)
      : product.id;
  const busy = state === "opening" || state === "confirming";
  const status: MessageKey | undefined =
    state === "opening"
      ? "profile.upgradePending"
      : state === "confirming"
        ? "profile.upgradeChecking"
        : state === "processing"
          ? "billing.sbp.processing"
          : state === "paid"
            ? "billing.sbp.paid"
            : state === "expired"
              ? "billing.sbp.expired"
              : state === "failed"
                ? errorKey
                : undefined;

  return (
    <div className="mt-3 flex flex-col gap-2">
      {state === "choosing" ? (
        <section
          aria-label={t("billing.sbp.chooserTitle")}
          className="flex flex-col"
        >
          <strong className="text-callout text-fg">
            {t("billing.sbp.chooserTitle")}
          </strong>
          <ul>
            {products.map((product) => {
              const price = product.prices.sbp;
              return (
                <li key={product.id}>
                  <ListRow
                    kind="setting"
                    title={title(product)}
                    trailing={
                      price
                        ? formatMinor(
                            price.amount_minor,
                            price.currency,
                            locale,
                          )
                        : undefined
                    }
                    chevron
                    onClick={() => {
                      void pay(product);
                    }}
                  />
                </li>
              );
            })}
          </ul>
          <Button
            variant="ghost"
            onClick={() => {
              setState("idle");
            }}
          >
            {t("billing.sbp.cancel")}
          </Button>
        </section>
      ) : (
        <Button
          variant="secondary"
          disabled={busy}
          onClick={() => {
            setState("choosing");
          }}
        >
          {t("billing.sbp.entry")}
        </Button>
      )}
      {status ? (
        <p role="status" className="text-caption text-fg-muted">
          {t(status)}
        </p>
      ) : null}
    </div>
  );
}
